/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module SharedModule
 */

import {
    Component,
    Input,
    OnInit,
} from '@angular/core';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { Observable, Subject } from 'rxjs';
import classnames from 'classnames';
import { difference } from 'underscore';
import {
    AviDropdownButtonPosition,
    BOTTOM_LEFT_ALIGNED_CONNECTED_POSITION,
    BOTTOM_RIGHT_ALIGNED_CONNECTED_POSITION,
    TOP_LEFT_ALIGNED_CONNECTED_POSITION,
    TOP_RIGHT_ALIGNED_CONNECTED_POSITION,
} from './avi-dropdown-button.constants';
import './avi-dropdown-button.component.less';

export interface IAviDropdownButtonAction {
    label: string;
    onClick(context?: any): void;
    disabled?(context?: any): boolean;
    hidden?(context?: any): boolean;
}

const connectedPositionMap = new Map([
    [AviDropdownButtonPosition.BOTTOM_RIGHT, BOTTOM_RIGHT_ALIGNED_CONNECTED_POSITION],
    [AviDropdownButtonPosition.BOTTOM_LEFT, BOTTOM_LEFT_ALIGNED_CONNECTED_POSITION],
    [AviDropdownButtonPosition.TOP_RIGHT, TOP_RIGHT_ALIGNED_CONNECTED_POSITION],
    [AviDropdownButtonPosition.TOP_LEFT, TOP_LEFT_ALIGNED_CONNECTED_POSITION],
]);

/**
 * @description Dropdown button component with auto-repositioning functionality. With the styles as
 * Clarity dropdown component.
 * @author alextsg, Zhiqian Liu
 */
@Component({
    selector: 'avi-dropdown-button',
    templateUrl: './avi-dropdown-button.component.html',
})
export class AviDropdownButtonComponent implements OnInit {
    /**
     * True to hide the dropdown caret.
     */
    @Input()
    public hideCaret = false;

    /**
     * List of actions shown in the dropdown menu.
     */
    @Input()
    public actions: IAviDropdownButtonAction[] = [];

    /**
     * True to use icon styling.
     */
    @Input()
    public useIcon = false;

    /**
     * True to not make menu automatically reposition according to the viewport.
     */
    @Input()
    public fixedPosition = false;

    /**
     * Disables and prevents interaction with the dropdown component.
     */
    @Input('disabled')
    private set setDisabled(disabled: boolean | '') {
        this.disabled = disabled === '' || disabled;
    }

    /**
     * @param position - Relative position to the button for showing the dropdown.
     */
    @Input('position')
    private set setPrimePosition(position: AviDropdownButtonPosition) {
        this.primePosition = position || AviDropdownButtonPosition.BOTTOM_RIGHT;
    }

    /**
     * Sets the size of the icon.
     */
    @Input()
    public size: 'sm' | 'md' = 'md';

    /**
     * Optional class name passed to each option element, ex. a 'sel-' class can be added.
     */
    @Input()
    public actionClassName?: string;

    /**
     * Passed to an action's onClick, disabled, and hidden functions as an additional param. Useful
     * when this component is dynamically/iteratively rendered and those properties are dependent
     * on additional factors. Ex. when this component is used to render singleactions in the
     * avi-data-grid, some actions may be hidden based on the row this component is part of.
     */
    @Input()
    public context?: any;

    /**
     * Disabled state of the button.
     */
    public disabled = false;

    /**
     * List of dropdown positions to prioritize.
     */
    public positionsPriority: ConnectedPosition[];

    /**
     * Observable to allow subscriptions to show and hide dropdown.
     */
    public dropdownButtonControl$: Observable<boolean>;

    /**
     * The position with the highest priority when re-positioning the dropdown. If fixedPosition is
     * set to true, this will serve as the only position.
     */
    private primePosition: AviDropdownButtonPosition = AviDropdownButtonPosition.BOTTOM_RIGHT;

    /**
     * Subject to control dropdown button.
     */
    private dropdownButtonControlSubject = new Subject<boolean>();

    constructor() {
        this.dropdownButtonControl$ = this.dropdownButtonControlSubject.asObservable();
    }

    /**
     * Get class names to be applied to the button element.
     */
    public get buttonClassName(): string {
        return classnames(
            this.useIcon && 'btn-icon btn-link avi-dropdown-button__button--icon',
            this.size === 'sm' && 'btn-sm',
        );
    }

    /** @override */
    public ngOnInit(): void {
        this.setPositionsPriority();
    }

    /**
     * Hide the dropdown.
     */
    public hideDropdown(): void {
        this.dropdownButtonControlSubject.next(false);
    }

    /**
     * Set the priority of positions for auto-reposition.
     */
    private setPositionsPriority(): void {
        const rightAlignedPositions = [
            BOTTOM_RIGHT_ALIGNED_CONNECTED_POSITION,
            TOP_RIGHT_ALIGNED_CONNECTED_POSITION,
        ];

        const leftAlignedPositions = [
            BOTTOM_LEFT_ALIGNED_CONNECTED_POSITION,
            TOP_LEFT_ALIGNED_CONNECTED_POSITION,
        ];

        const primeConnectedPosition = connectedPositionMap.get(this.primePosition);

        if (this.fixedPosition) {
            this.positionsPriority = [primeConnectedPosition];
        } else {
            const isPrimePositionLeftAligned =
                leftAlignedPositions.includes(primeConnectedPosition);

            if (isPrimePositionLeftAligned) {
                // set primePosition to the top of the priority list
                this.positionsPriority = [
                    primeConnectedPosition,
                    ...difference(leftAlignedPositions, [primeConnectedPosition]),
                    ...rightAlignedPositions,
                ];
            } else { // primePosition is right-aligned
                this.positionsPriority = [
                    primeConnectedPosition,
                    ...difference(rightAlignedPositions, [primeConnectedPosition]),
                    ...leftAlignedPositions,
                ];
            }
        }
    }
}
